import { useState } from 'react';

const Scholarships = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const scholarshipPrograms = [
    {
      id: 1,
      name: 'Full Tuition Scholarship',
      amount: 'Full tuition coverage',
      description: 'Covers complete tuition for one academic year for exceptional students with financial need.',
      eligibility: [
        'Demonstrated financial need',
        'Exceptional ballet technique and potential',
        'Ages 10-18',
        'Commitment to full academic year'
      ],
      benefits: [
        'Full tuition coverage',
        'Access to all regular classes',
        'Performance opportunities',
        'Mentorship program'
      ],
      deadline: 'March 15, 2024'
    },
    {
      id: 2,
      name: 'Partial Tuition Scholarship',
      amount: '25-75% tuition coverage',
      description: 'Provides partial tuition assistance to help make ballet education more accessible.',
      eligibility: [
        'Financial need demonstrated',
        'Good ballet technique',
        'Ages 6-18',
        'Regular attendance commitment'
      ],
      benefits: [
        '25-75% tuition reduction',
        'Access to scholarship classes',
        'Performance opportunities',
        'Progress evaluations'
      ],
      deadline: 'April 1, 2024'
    },
    {
      id: 3,
      name: 'Merit-Based Scholarship',
      amount: 'Up to 50% tuition coverage',
      description: 'Awarded to students who demonstrate exceptional talent and dedication to ballet.',
      eligibility: [
        'Outstanding ballet technique',
        'Strong work ethic and attitude',
        'Ages 8-18',
        'Previous ballet training'
      ],
      benefits: [
        'Up to 50% tuition coverage',
        'Advanced class placement',
        'Solo performance opportunities',
        'Master class access'
      ],
      deadline: 'March 30, 2024'
    },
    {
      id: 4,
      name: 'Community Service Scholarship',
      amount: '25% tuition coverage',
      description: 'For students who demonstrate commitment to community service and outreach.',
      eligibility: [
        'Active community service participation',
        'Good ballet technique',
        'Ages 12-18',
        'Leadership qualities'
      ],
      benefits: [
        '25% tuition reduction',
        'Community outreach participation',
        'Leadership development',
        'Service learning opportunities'
      ],
      deadline: 'April 15, 2024'
    }
  ];

  const applicationSteps = [
    {
      step: 1,
      title: 'Submit Application',
      description: 'Complete the online scholarship application form with all required information.',
      icon: '📝'
    },
    {
      step: 2,
      title: 'Financial Documentation',
      description: 'Provide required financial documents to demonstrate need (if applicable).',
      icon: '📊'
    },
    {
      step: 3,
      title: 'Audition',
      description: 'Attend a scholarship audition to demonstrate your ballet technique and potential.',
      icon: '🩰'
    },
    {
      step: 4,
      title: 'Interview',
      description: 'Participate in a brief interview with our scholarship committee.',
      icon: '💬'
    },
    {
      step: 5,
      title: 'Notification',
      description: 'Receive notification of scholarship decision and next steps.',
      icon: '📧'
    }
  ];

  const testimonials = [
    {
      name: 'Emma Rodriguez',
      age: 16,
      scholarship: 'Full Tuition Scholarship Recipient',
      quote: 'The scholarship program has made my ballet dreams possible. I\'m so grateful for this opportunity to train at such a high level.',
      image: '/api/placeholder/80/80'
    },
    {
      name: 'Michael Chen',
      age: 14,
      scholarship: 'Merit-Based Scholarship Recipient',
      quote: 'This scholarship has allowed me to focus on my training without worrying about the financial burden on my family.',
      image: '/api/placeholder/80/80'
    },
    {
      name: 'Sofia Martinez',
      age: 12,
      scholarship: 'Partial Tuition Scholarship Recipient',
      quote: 'I love being part of the NBT family. The scholarship program shows how much they care about their students.',
      image: '/api/placeholder/80/80'
    }
  ];

  return (
    <main className="min-h-screen bg-ballet-light">
      {/* Hero Section */}
      <div className="bg-elegant-gradient text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-5xl lg:text-6xl font-elegant font-bold mb-6">
            Scholarship Programs
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
            Making quality ballet education accessible to talented students regardless of financial circumstances
          </p>
          <button className="btn-primary bg-white text-ballet-burgundy hover:bg-ballet-light">
            Apply for Scholarship
          </button>
        </div>
      </div>

      <div className="container-custom section-padding">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap justify-center mb-12 bg-white rounded-2xl p-2 shadow-elegant">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'programs', label: 'Programs' },
            { id: 'application', label: 'How to Apply' },
            { id: 'testimonials', label: 'Student Stories' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-ballet-burgundy text-white shadow-lg'
                  : 'text-ballet-burgundy hover:bg-ballet-light'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-12">
            <div className="card-elevated">
              <div className="p-8 lg:p-12 text-center">
                <h2 className="text-3xl font-elegant font-bold gradient-text mb-6">
                  Our Commitment to Accessibility
                </h2>
                <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed mb-8">
                  At Northwest Ballet Theater, we believe that financial circumstances should never 
                  prevent a talented student from pursuing their passion for ballet. Our comprehensive 
                  scholarship program ensures that deserving students have access to high-quality 
                  ballet education and performance opportunities.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-ballet-light p-6 rounded-xl">
                    <div className="text-4xl mb-4">🎓</div>
                    <h3 className="text-xl font-semibold text-ballet-burgundy mb-2">$50,000+</h3>
                    <p className="text-gray-700">Annual scholarship funding provided to students</p>
                  </div>
                  <div className="bg-ballet-light p-6 rounded-xl">
                    <div className="text-4xl mb-4">👥</div>
                    <h3 className="text-xl font-semibold text-ballet-burgundy mb-2">25+</h3>
                    <p className="text-gray-700">Students receive scholarship support each year</p>
                  </div>
                  <div className="bg-ballet-light p-6 rounded-xl">
                    <div className="text-4xl mb-4">⭐</div>
                    <h3 className="text-xl font-semibold text-ballet-burgundy mb-2">100%</h3>
                    <p className="text-gray-700">Of scholarship recipients show significant improvement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Programs Tab */}
        {activeTab === 'programs' && (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-elegant font-bold gradient-text mb-6">
                Available Scholarship Programs
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                We offer several scholarship opportunities to meet different needs and circumstances.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {scholarshipPrograms.map((program) => (
                <div key={program.id} className="card-elevated">
                  <div className="p-8">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy">
                        {program.name}
                      </h3>
                      <span className="bg-ballet-light text-ballet-burgundy px-3 py-1 rounded-full text-sm font-semibold">
                        {program.amount}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-6">{program.description}</p>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-ballet-burgundy mb-3">Eligibility Requirements:</h4>
                      <ul className="space-y-2">
                        {program.eligibility.map((req, index) => (
                          <li key={index} className="flex items-center text-gray-700">
                            <svg className="w-4 h-4 text-ballet-burgundy mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-ballet-burgundy mb-3">Benefits Include:</h4>
                      <ul className="space-y-2">
                        {program.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-center text-gray-700">
                            <svg className="w-4 h-4 text-ballet-burgundy mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        <strong>Deadline:</strong> {program.deadline}
                      </span>
                      <button className="btn-primary">
                        Apply Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Application Tab */}
        {activeTab === 'application' && (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl font-elegant font-bold gradient-text mb-6">
                Application Process
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Follow these steps to apply for a scholarship at Northwest Ballet Theater.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {applicationSteps.map((step, index) => (
                <div key={step.step} className="text-center">
                  <div className="relative">
                    <div className="w-16 h-16 bg-ballet-burgundy rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-lg">{step.step}</span>
                    </div>
                    {index < applicationSteps.length - 1 && (
                      <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-ballet-light transform -translate-y-1/2"></div>
                    )}
                  </div>
                  <div className="text-3xl mb-3">{step.icon}</div>
                  <h3 className="font-semibold text-ballet-burgundy mb-2">{step.title}</h3>
                  <p className="text-gray-700 text-sm">{step.description}</p>
                </div>
              ))}
            </div>

            <div className="card-elevated">
              <div className="p-8">
                <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-6 text-center">
                  Important Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-ballet-burgundy mb-3">Required Documents:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Completed application form</li>
                      <li>• Financial need documentation (if applicable)</li>
                      <li>• Dance resume or experience summary</li>
                      <li>• Two letters of recommendation</li>
                      <li>• Recent photo (headshot preferred)</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-ballet-burgundy mb-3">Audition Requirements:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Ballet class participation</li>
                      <li>• Prepared variation (advanced students)</li>
                      <li>• Improvisation exercise</li>
                      <li>• Brief interview</li>
                      <li>• Appropriate dance attire required</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-8 text-center">
                  <button className="btn-primary mr-4">
                    Download Application
                  </button>
                  <button className="btn-outline">
                    Schedule Audition
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl font-elegant font-bold gradient-text mb-6">
                Student Success Stories
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Hear from our scholarship recipients about their experiences at Northwest Ballet Theater.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="card-elevated">
                  <div className="p-6 text-center">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-20 h-20 rounded-full mx-auto mb-4 object-cover"
                    />
                    <h3 className="text-xl font-elegant font-bold text-ballet-burgundy mb-1">
                      {testimonial.name}
                    </h3>
                    <p className="text-gray-600 mb-2">Age {testimonial.age}</p>
                    <p className="text-sm text-ballet-burgundy font-semibold mb-4">
                      {testimonial.scholarship}
                    </p>
                    <blockquote className="text-gray-700 italic">
                      "{testimonial.quote}"
                    </blockquote>
                  </div>
                </div>
              ))}
            </div>

            <div className="card-elevated">
              <div className="p-8 text-center">
                <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-4">
                  Ready to Apply?
                </h3>
                <p className="text-gray-700 mb-6">
                  Don't let financial barriers prevent you from pursuing your ballet dreams. 
                  Apply for a scholarship today and join our supportive dance community.
                </p>
                <button className="btn-primary">
                  Start Your Application
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default Scholarships;
